import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { LIGA_POR_ID, LigaPorIdData, LigaPorIdVars } from '../../services/ligaService';
import { useProfile } from '../../context/ProfileContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import LogoutConfirmationDialog from '../../components/LogoutConfirmationDialog';
import Load from '../../components/Load';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';

interface DashboardOption {
  icon: string;
  name: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
}

const LeagueDashboard: React.FC = () => {
  const { currentProfile, openSelectionModal, isLoading: profileLoading } = useProfile();
  const auth = useAuth();
  const navigate = useNavigate();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  // Calcula ligaId antes de usar no useQuery
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  // Busca dados da liga - hook deve ser chamado antes de qualquer early return
  const { data, loading, error } = useQuery<LigaPorIdData, LigaPorIdVars>(LIGA_POR_ID, {
    variables: { id: ligaId! },
    skip: !ligaId || profileLoading || !currentProfile || currentProfile.tipo !== 'LIGA',
  });

  // Verificar permissões do organizador
  const { canEditLiga, canManageTournaments, canManagePayments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada (redirecionamento será feito pelo ProfileRedirectHandler)
  // Garante que currentProfile não é null e é do tipo LIGA
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  const liga = data?.ligaPorId;

  const handleTrocarPerfil = () => {
    openSelectionModal();
  };

  const handleSair = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    auth?.logout();
    navigate('/login', { replace: true });
  };

  const handleCloseLogoutDialog = () => {
    setIsLogoutDialogOpen(false);
  };

  const dashboardOptions: DashboardOption[] = [
    {
      icon: 'bi-people',
      name: 'Membros',
      description: 'Gerenciar membros da liga',
      disabled: false,
      onClick: () => navigate('/liga/members'),
    },
    {
      icon: 'bi-trophy',
      name: 'Torneios',
      description: canManageTournaments ? 'Gerenciar torneios da liga' : 'Gerenciar torneios da liga (Apenas Administradores e Gerentes)',
      disabled: !canManageTournaments,
      onClick: canManageTournaments ? () => navigate('/liga/tournaments') : undefined,
    },
    {
      icon: 'bi-tags',
      name: 'Categorias',
      description: canManageTournaments
        ? 'Gerenciar categorias reutilizáveis da liga'
        : 'Gerenciar categorias reutilizáveis da liga (Apenas Administradores e Gerentes)',
      disabled: !canManageTournaments,
      onClick: canManageTournaments ? () => navigate('/liga/categorias') : undefined,
    },
    {
      icon: 'bi-cash-stack',
      name: 'Pagamentos',
      description: canManagePayments
        ? 'Gerenciar pagamentos da liga'
        : 'Gerenciar pagamentos da liga (Apenas Administradores e Financeiros)',
      disabled: !canManagePayments,
      onClick: canManagePayments ? () => navigate('/liga/financeiro') : undefined,
    },
    {
      icon: 'bi-pencil',
      name: 'Editar Liga',
      description: canEditLiga ? 'Editar informações da liga' : 'Editar informações da liga (Apenas Administradores)',
      disabled: !canEditLiga,
      onClick: canEditLiga ? () => navigate('/liga/edit') : undefined,
    },
    {
      icon: 'bi-arrow-repeat',
      name: 'Trocar Perfil',
      description: 'Selecionar outro perfil',
      onClick: handleTrocarPerfil,
    },
    {
      icon: 'bi-box-arrow-right',
      name: 'Sair',
      description: 'Fazer logout da conta',
      onClick: handleSair,
    },
  ];

  if (loading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !liga) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        {error ? 'Erro ao carregar informações da liga.' : 'Liga não encontrada.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const avatarUrl = liga.imagemUrl || '/assets/images/user.png';

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                {/* Seção superior com informações da liga */}
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center flex-column flex-md-row">
                      <figure className="avatar me-md-4 mb-3 mb-md-0 w100" style={{ maxWidth: '120px' }}>
                        <img
                          src={avatarUrl}
                          alt={liga.nome}
                          className="float-right p-1 bg-white rounded-circle w-100"
                          style={{ objectFit: 'cover' }}
                        />
                      </figure>
                      <div className="text-center text-md-start">
                        <h3 className="fw-700 mb-1">{liga.nome}</h3>
                        <p className="text-grey-500 font-xsss mb-0">Painel de Gerenciamento</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grid de opções */}
                <div className="row">
                  {dashboardOptions.map((option, index) => (
                    <div key={index} className="col-lg-4 col-md-6 col-sm-12 mb-3">
                      <div
                        className={`card w-100 shadow-xss rounded-xxl border-0 ${
                          option.disabled ? 'opacity-75' : 'cursor-pointer'
                        }`}
                        onClick={option.disabled ? undefined : option.onClick}
                        role={option.disabled ? undefined : 'button'}
                        aria-label={option.disabled ? `${option.name} - ${option.description} (Indisponível)` : `${option.name} - ${option.description}`}
                        tabIndex={option.disabled ? undefined : 0}
                        onKeyDown={(e) => {
                          if (!option.disabled && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            option.onClick?.();
                          }
                        }}
                        style={{ 
                          cursor: option.disabled ? 'not-allowed' : 'pointer',
                          minHeight: '150px',
                          transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!option.disabled) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div className="card-body p-4">
                          <div className="d-flex align-items-start">
                            <div className="me-3">
                              <i
                                className={`bi ${option.icon} btn-round-lg bg-primary text-white font-md d-inline-block`}
                                style={{ width: '48px', height: '48px', lineHeight: '48px', textAlign: 'center' }}
                              ></i>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fw-600 mb-1">{option.name}</h5>
                              <p className="text-grey-500 font-xsss mb-0">{option.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog de confirmação de logout */}
      <LogoutConfirmationDialog
        open={isLogoutDialogOpen}
        onClose={handleCloseLogoutDialog}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
};

export default LeagueDashboard;

